import { isValid } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Truck, FileText } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/styles/useTheme';
import CssBaseline from '@mui/material/CssBaseline';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Backdrop from '@mui/material/Backdrop';
import { CircularProgress } from '@mui/material';
import { newDate, locales } from '../../../utils/formatters';
import { useContactApi, useOrdersApi } from '../../../apis';
import { parseApiResponseData } from '../../../apis/Utilities/parseApiResponseData';
import BackButton from '../../../components/BackButton';
import Download from '../../../components/Download';
import OrderDetailItemCard from './OrderDetailItemCard';
import OrderDetailStepper from './Stepper';
import ShippingDetailsSection from './ShippingDetailsSection';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import OrderDetailContactCard from './OrderDetailContactCard';
import Error from '../../../components/Error';
import Success from '../../../components/Success';
import PageContentContainer from '../../../components/PageContentContainer';
import OrderAddressBlock from '../../../components/OrderAddressBlock';

function OrderDetails({ namespace }) {
	const { t } = useTranslation(namespace, 'orders:columns', 'download');
	const theme = useTheme();
	const isMountedRef = useIsMountedRef();
	const [order, setOrder] = useState(undefined);
	const [csr, setCsr] = useState([]);
	const { source, orderId } = useParams();
	const [success, setSuccess] = useState(null);
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isError, setIsError] = useState(false);
	const { getDetails: getOrderDetails } = useOrdersApi();
	const { getCsrDetails } = useContactApi();

	const fetchOrder = useCallback(async () => {
		setIsLoading(true);
		try {
			const resp = await getOrderDetails({ id: orderId, source });

			if (resp?.isAborted) {
				setIsLoading(false);
				return;
			}

			if (resp?.isError) {
				console.log('err', resp.error || 'failed to load order details');
				setIsError(true);
				setIsLoading(false);
				return;
			}

			if (isMountedRef.current) {
				const data = parseApiResponseData(resp, {})?.Orders?.[0];
				if (!data) {
					setIsError(true);
					setIsLoading(false);
					return;
				}
				setOrder(data);
				const response = await getCsrDetails({
					PlantCode: data.plant_Code,
					BillToCustomerNumber: data.bill_To_Customer_Number,
					ShipToNumber: data.ship_To_Customer_Number,
				});

				if (response?.isAborted) {
					setIsLoading(false);
					return;
				}

				if (response?.isError) {
					console.log('err', response.error || 'failed to load csr details');
					setIsError(true);
					setIsLoading(false);
					return;
				}
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'view_order',
					transaction_id: data.order_Number,
					items: [
						{
							id: data.customer_Item_Number,
							name: data.item_Description,
							quantity: data.quantity_Ordered,
						},
					],
					order_date: data.order_Date,
					due_date: data.order_Due_Date,
				});
				const csrData = parseApiResponseData(response, {})?.results?.[0];
				setCsr(csrData || []);
			}
		} catch (err) {
			console.log('err', err);
			setIsError(true);
		}
		setIsLoading(false);
	}, [isMountedRef, source, orderId, getCsrDetails, getOrderDetails]);

	const ORDER_STEP_STATE = Object.freeze({
		NONE: 0,
		ORDERED: 1,
		IN_PROGRESS: 2,
		SHIPPED: 3,
		DELIVERED: 4,
		INVOICED: 5,
	});

	/* ============================================================================ */
	/**
	 * Canonical status keys used across order status/date logic.
	 *
	 * @constant
	 */
	/* ============================================================================ */
	const ORDER_STATUS = Object.freeze({
		UNKNOWN: 'UNKNOWN',
		ORDERED: 'ORDERED',
		IN_PROGRESS: 'IN_PROGRESS',
		SHIPPED: 'SHIPPED',
		DELIVERED: 'DELIVERED',
		INVOICED: 'INVOICED',
		CANCELLED: 'CANCELLED',
		ALERT: 'ALERT',
	});

	/* ============================================================================ */
	/**
	 * Normalizes raw status input to a canonical ORDER_STATUS value.
	 *
	 * @param {string} value Raw status value from API or UI.
	 * @returns {string} Canonical status key from ORDER_STATUS.
	 */
	/* ============================================================================ */
	const normalizeOrderStatus = (value) => {
		if (!value || typeof value !== 'string') {
			return ORDER_STATUS.UNKNOWN;
		}

		const normalized = value
			.trim()
			.toLowerCase()
			.replace(/[_-]+/g, ' ')
			.replace(/\s+/g, ' ');

		switch (normalized) {
			case 'ordered':
				return ORDER_STATUS.ORDERED;
			case 'in progress':
			case 'inprogress':
				return ORDER_STATUS.IN_PROGRESS;
			case 'shipped':
				return ORDER_STATUS.SHIPPED;
			case 'delivered':
				return ORDER_STATUS.DELIVERED;
			case 'invoiced':
				return ORDER_STATUS.INVOICED;
			case 'cancelled':
			case 'canceled':
				return ORDER_STATUS.CANCELLED;
			case 'alert':
				return ORDER_STATUS.ALERT;
			default:
				return ORDER_STATUS.UNKNOWN;
		}
	};

	/* ============================================================================ */
	/**
	 * Maps a canonical status to the numeric stepper index value.
	 *
	 * @param {string} status Canonical status key from ORDER_STATUS.
	 * @returns {number} Numeric step value from ORDER_STEP_STATE.
	 */
	/* ============================================================================ */
	const getStepStateFromStatus = (status) => {
		switch (status) {
			case ORDER_STATUS.ORDERED:
				return ORDER_STEP_STATE.ORDERED;
			case ORDER_STATUS.IN_PROGRESS:
				return ORDER_STEP_STATE.IN_PROGRESS;
			case ORDER_STATUS.SHIPPED:
				return ORDER_STEP_STATE.SHIPPED;
			case ORDER_STATUS.DELIVERED:
				return ORDER_STEP_STATE.DELIVERED;
			case ORDER_STATUS.INVOICED:
				return ORDER_STEP_STATE.INVOICED;
			default:
				return ORDER_STEP_STATE.NONE;
		}
	};

	/* ============================================================================ */
	/**
	 * Derives the current active step from reached-status booleans.
	 *
	 * @param {Object} params Step reached flags keyed by timeline stage.
	 * @param {boolean} params.reachedOrdered Ordered stage reached.
	 * @param {boolean} params.reachedInProcess In progress stage reached.
	 * @param {boolean} params.reachedShipped Shipped stage reached.
	 * @param {boolean} params.reachedDelivered Delivered stage reached.
	 * @param {boolean} params.reachedInvoiced Invoiced stage reached.
	 * @returns {number} Highest resolved step value from ORDER_STEP_STATE.
	 */
	/* ============================================================================ */
	const getActiveStepFromReached = ({
		reachedOrdered,
		reachedInProcess,
		reachedShipped,
		reachedDelivered,
		reachedInvoiced,
	}) => {
		if (reachedInvoiced) {
			return ORDER_STEP_STATE.INVOICED;
		}
		if (reachedDelivered) {
			return ORDER_STEP_STATE.DELIVERED;
		}
		if (reachedShipped) {
			return ORDER_STEP_STATE.SHIPPED;
		}
		if (reachedInProcess) {
			return ORDER_STEP_STATE.IN_PROGRESS;
		}
		if (reachedOrdered) {
			return ORDER_STEP_STATE.ORDERED;
		}

		return ORDER_STEP_STATE.NONE;
	};

	useEffect(() => {
		fetchOrder();
		localStorage.getItem('success');
		window.addEventListener('storage', (e) => {
			setSuccess(localStorage.getItem('success'));
		});
		localStorage.getItem('error');
		window.addEventListener('storage', (e) => {
			setError(localStorage.getItem('error'));
		});
	}, [fetchOrder]);

	if (!order || !csr) {
		return null;
	}

	/* ============================================================================ */
	/**
	 * Resolves whether this order should be displayed as Alert or Cancelled.
	 *
	 * @returns {string|null} ORDER_STATUS.ALERT, ORDER_STATUS.CANCELLED, or null.
	 */
	/* ============================================================================ */
	const getAlertCancelStatus = () => {
		const orderStatus = (order.orderStatusHistory || []).map((item) =>
			normalizeOrderStatus(item.order_Status)
		);

		if (
			orderStatus.includes(ORDER_STATUS.ALERT) &&
			!(
				orderStatus.includes(ORDER_STATUS.IN_PROGRESS) ||
				orderStatus.includes(ORDER_STATUS.SHIPPED) ||
				orderStatus.includes(ORDER_STATUS.DELIVERED) ||
				orderStatus.includes(ORDER_STATUS.INVOICED)
			)
		) {
			return ORDER_STATUS.ALERT;
		} else if (
			orderStatus.includes(ORDER_STATUS.CANCELLED) &&
			normalizeOrderStatus(order.order_Status) === ORDER_STATUS.CANCELLED
		) {
			return ORDER_STATUS.CANCELLED;
		}
		return null;
	};

	/* ============================================================================ */
	/**
	 * Returns the status timestamp for a shipment BOL and step type.
	 *
	 * @param {string} bol Bill of lading identifier.
	 * @param {string} stepType Raw or canonical step type value.
	 * @returns {string} Matching timestamp value, or empty string when unavailable.
	 */
	/* ============================================================================ */
	const getStatusDate = (bol, stepType) => {
		const statusType = normalizeOrderStatus(stepType);
		let stepDateData = '';

		if (!order.orderStatusHistory && !order.shipingDetails) {
			return '';
		}

		// Special case for 'Shipped' to use shipingDetails.ship_Date when available.
		if (statusType === ORDER_STATUS.SHIPPED && order.shipingDetails) {
			for (let i = 0; i < order.shipingDetails.length; i += 1) {
				const shippingDetail = order.shipingDetails[i];
				const shippingBol = shippingDetail.boL_Number || shippingDetail.bol;

				if (shippingBol === bol) {
					stepDateData = shippingDetail.ship_Date || '';
					return stepDateData;
				}
			}

			// Never fall back to orderStatusHistory for shipped when ship date is missing.
			return '';
		}

		//  Special case for 'Delivered' as it may NOT be in orderStatusHistory.
		//  Use shipingDetails.Delivery_TimeStamp_UTC if available- It may have one, both or no value
		if (statusType === ORDER_STATUS.DELIVERED && order.shipingDetails) {
			let d;

			for (let i = 0; i < order.shipingDetails.length; i += 1) {
				if (
					order.shipingDetails[i].delivery_TimeStamp_UTC !== null &&
					order.shipingDetails[i].boL_Number === bol
				) {
					d = new Date(order.shipingDetails[i].delivery_TimeStamp_UTC);
				} else if (
					order.shipingDetails[i].confirmed_Delivery_Date !== null &&
					order.shipingDetails[i].boL_Number === bol
				) {
					d = new Date(order.shipingDetails[i].confirmed_Delivery_Date);
				}
				if (isValid(d)) {
					const stepDate = new Date(`${d}Z`).toLocaleString(locales(), {
						hour12: true,
					});
					return stepDate;
				}
			}
		}

		for (let i = 0; i < order.orderStatusHistory.length; i += 1) {
			if (
				order.orderStatusHistory[i].boL_Number === bol &&
				normalizeOrderStatus(order.orderStatusHistory[i].order_Status) ===
					statusType
			) {
				stepDateData = order.orderStatusHistory[i].date_Occurred;
			}
		}
		return stepDateData;
	};

	/* ============================================================================ */
	/**
	 * Returns order-level status date for status-history-only scenarios.
	 *
	 * @param {string} stepType Raw or canonical step type value.
	 * @returns {string} Matching status timestamp, or empty string when unavailable.
	 */
	/* ============================================================================ */
	const getOrderStatusDate = (stepType) => {
		const statusType = normalizeOrderStatus(stepType);
		let stepDateData = '';
		if (!order.orderStatusHistory) {
			return '';
		}
		const unique = [
			...new Map(
				order.orderStatusHistory.map((m) => [
					normalizeOrderStatus(m.order_Status),
					m.date_Occurred,
				])
			).values(),
		].filter((n) => n);

		if (statusType === ORDER_STATUS.IN_PROGRESS) {
			for (let i = 0; i < order.orderStatusHistory.length; i += 1) {
				if (
					normalizeOrderStatus(order.orderStatusHistory[i].order_Status) ===
					ORDER_STATUS.IN_PROGRESS
				) {
					return order.orderStatusHistory[i].date_Occurred;
				}
			}

			if (unique.length) {
				return unique.sort()[unique.sort().length - 1];
			}

			return '';
		}

		for (let i = 0; i < order.orderStatusHistory.length; i += 1) {
			if (
				normalizeOrderStatus(order.orderStatusHistory[i].order_Status) ===
				statusType
			) {
				stepDateData = order.orderStatusHistory[i].date_Occurred;
				break;
			}
		}

		return stepDateData;
	};

	/* ============================================================================ */
	/**
	 * Determines whether a shipment BOL should be treated as Shipped or Cancelled.
	 *
	 * @param {string} bol Bill of lading identifier.
	 * @returns {string} ORDER_STATUS.SHIPPED or ORDER_STATUS.CANCELLED.
	 */
	/* ============================================================================ */
	const getCancelShippingStatus = (bol) => {
		let stepDateData = ORDER_STATUS.CANCELLED;
		if (!order.shipingDetails) {
			return '';
		}
		for (let i = 0; i < order.shipingDetails.length; i += 1) {
			if (
				normalizeOrderStatus(order.shipingDetails[i].status) !==
					ORDER_STATUS.CANCELLED &&
				order.shipingDetails[i].boL_Number === bol
			) {
				stepDateData = ORDER_STATUS.SHIPPED;
			}
		}

		return stepDateData;
	};

	/* ============================================================================ */
	/**
	 * Determines whether status history has reached a given step, optionally by BOL.
	 *
	 * @param {string} stepType Raw or canonical step type value.
	 * @param {string} [bol] Optional bill of lading filter.
	 * @returns {boolean} True when a matching status-history record exists.
	 */
	/* ============================================================================ */
	const hasReachedStatus = (stepType, bol) => {
		const statusType = normalizeOrderStatus(stepType);

		if (!order.orderStatusHistory) {
			return false;
		}

		for (let i = 0; i < order.orderStatusHistory.length; i += 1) {
			if (
				(bol ? order.orderStatusHistory[i].boL_Number === bol : true) &&
				normalizeOrderStatus(order.orderStatusHistory[i].order_Status) ===
					statusType
			) {
				return true;
			}
		}

		return false;
	};

	/* ============================================================================ */
	/**
	 * Builds reached-status flags from status history, optionally filtered by BOL.
	 *
	 * @param {string} [bol] Optional bill of lading filter.
	 * @returns {{
	 * 	reachedOrdered: boolean,
	 * 	reachedInProcess: boolean,
	 * 	reachedShipped: boolean,
	 * 	reachedDelivered: boolean,
	 * 	reachedInvoiced: boolean,
	 * }} Reached flags for each timeline status.
	 */
	/* ============================================================================ */
	const getStatusHistoryReached = (bol) => ({
		reachedOrdered: hasReachedStatus(ORDER_STATUS.ORDERED, bol),
		reachedInProcess: hasReachedStatus(ORDER_STATUS.IN_PROGRESS, bol),
		reachedShipped: hasReachedStatus(ORDER_STATUS.SHIPPED, bol),
		reachedDelivered: hasReachedStatus(ORDER_STATUS.DELIVERED, bol),
		reachedInvoiced: hasReachedStatus(ORDER_STATUS.INVOICED, bol),
	});

	/* ============================================================================ */
	/**
	 * Derives active step from status-history reached flags, optionally by BOL.
	 *
	 * @param {string} [bol] Optional bill of lading filter.
	 * @returns {number} Numeric active step value.
	 */
	/* ============================================================================ */
	const getStatusHistoryActiveStep = (bol) =>
		getActiveStepFromReached(getStatusHistoryReached(bol));

	const Item = styled(Paper)(() => ({
		backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
		...theme.typography.body2,
		padding: theme.spacing(1),
		textAlign: 'center',
		// color: theme.palette.text.secondary,
	}));

	const showShippingDetails = order && order.shipingDetails;
	const showStatusHistory =
		!showShippingDetails && order && order.orderStatusHistory;
	const alertCancelStatus = getAlertCancelStatus();

	return (
		<>
			<CssBaseline />
			<Backdrop
				role="progressbar"
				sx={{
					color: 'primary',
					zIndex: theme.zIndex.drawer + 1,
					backgroundColor: 'transparent',
				}}
				open={isLoading}
			>
				<CircularProgress
					color="inherit"
					role="presentation"
					key={Math.random()}
				/>
			</Backdrop>
			{order && order && !isLoading ? (
				<Container
					maxWidth="xl"
					style={{ padding: `0 0 ${theme.spacing(8.5)} 0` }}
					aria-label={t('order_details')}
				>
					<Grid item xs={12} aria-label={t('order_details')}>
						<PageContentContainer ariaLabel={t('order_details')} tabIndex={-1}>
							{!error && !success && (
								<Grid item xs={2} aria-label={t('back')} mt={-0.75}>
									<BackButton />
								</Grid>
							)}
							{!!error ||
								(!!success && (
									<Grid
										xs={5}
										sx={{
											display: { xs: 'none', sm: 'block' },
											width: '100%',
											marginLeft: '-.65rem',
										}}
										aria-label={t('error')}
									>
										{error && !success && (
											<Error
												aria-label={t('download:error')}
												tabIndex={-1}
												role="paragraph"
												message={t('download:error')}
											/>
										)}

										{success && (
											<Success
												aria-label={t('download:success')}
												tabIndex={-1}
												role="paragraph"
												message={t('download:success')}
											/>
										)}
									</Grid>
								))}
							<Grid item xs={12}>
								<Typography
									variant="h2"
									aria-label={t('PO')}
									role="paragraph"
									style={{ lineHeight: theme.palette.lineHeight.Lg }}
								>
									{order.pO_Number ? `${t('PO')}  ${order.pO_Number}` : '-'}
								</Typography>
							</Grid>
							<Grid item xs={12} mt={-1.75}>
								<Typography
									style={{ fontSize: theme.palette.textSize.sizeLg }}
									aria-label={t('wrk_order_number')}
									role="paragraph"
								>
									{t('wrk_order_number')}{' '}
									{order.order_Number ? order.order_Number : '-'}
								</Typography>
							</Grid>
						</PageContentContainer>
					</Grid>
					<Grid item xs={12} aria-label={t('wrk_order_number')}>
						<Grid
							container
							style={{
								padding: `0 ${theme.spacing(4)}`,
								marginTop: theme.spacing(-0.75),
							}}
							aria-label={t('wrk_order_number')}
						>
							<Grid item xs={12} aria-label={t('wrk_order_number')}>
								<Truck
									style={{
										fontSize: theme.palette.font.size6,
										fontWeight: theme.palette.font.weightBold,
										marginBottom: '-.33rem',
										marginRight: '.52rem',
									}}
									aria-label={t('wrk_order_number')}
									role="generic"
									aria-hidden="true"
								/>
								<Typography
									variant="heading9"
									aria-label={t('wrk_order_number')}
									role="paragraph"
								>
									{t('shipment_tracking')}
								</Typography>
								<Divider
									style={{ marginTop: '.5rem' }}
									aria-label={t('shipment_tracking')}
									tabIndex={-1}
									role="presentation"
								/>
							</Grid>
						</Grid>
					</Grid>

					{showShippingDetails &&
						showShippingDetails.map((detail, i) => {
							const shippingStatus = getCancelShippingStatus(detail.boL_Number);
							const shipmentDates = {
								dateOrdered: getStatusDate(
									detail.boL_Number,
									ORDER_STATUS.ORDERED
								),
								dateInProcess: getStatusDate(
									detail.boL_Number,
									ORDER_STATUS.IN_PROGRESS
								),
								dateShipped: getStatusDate(detail.boL_Number, shippingStatus),
								dateDelivered: getStatusDate(
									detail.boL_Number,
									ORDER_STATUS.DELIVERED
								),
								dateInvoiced: getStatusDate(
									detail.boL_Number,
									ORDER_STATUS.INVOICED
								),
							};
							const statusHistoryReached = getStatusHistoryReached(
								detail.boL_Number
							);
							const statusHistoryActiveStep = getStatusHistoryActiveStep(
								detail.boL_Number
							);

							return (
								<React.Fragment key={detail.bol || i}>
									<Grid
										item
										xs={12}
										style={{
											marginTop: theme.spacing(-4),
										}}
										aria-label={t('shipment')}
									>
										<Grid
											container
											style={{
												padding: theme.spacing(4),
											}}
											aria-label={t('shipment')}
										>
											<Grid item xs={12} aria-label={t('shipment')}>
												<Grid container aria-label={t('shipment')}>
													<Grid item xs={12}>
														<Grid
															container
															direction="row"
															justifyContent="left"
															alignItems="flex-start"
															aria-label={t('shipment')}
														>
															<Grid
																item
																xs={10}
																sm={6}
																aria-label={t('shipment')}
															>
																<Grid
																	container
																	variant="heading8"
																	style={{
																		marginTop: `${theme.spacing(2.5)}`,
																		marginBottom: `${theme.spacing(2.75)}`,
																	}}
																	aria-label={t('shipment')}
																>
																	<Grid item aria-label={t('shipment')}>
																		<Typography
																			aria-label={t('shipment')}
																			role="paragraph"
																		>
																			{t('shipment')} {i + 1}
																		</Typography>
																	</Grid>
																</Grid>
															</Grid>
														</Grid>
													</Grid>
													<Grid
														item
														xs={12}
														sx={{
															overflowX: 'auto',
															width: '100%',
															maxWidth: {
																xs: '100%',
																sm: '100%',
																md: `calc(100vw - 25.75rem)`,
															},
															marginTop: theme.spacing(-1),
														}}
														aria-label={t('shipment')}
													>
														<Grid
															container
															direction="row"
															justifyContent="left"
															alignItems="flex-start"
															aria-label={t('shipment')}
														>
															<Grid item xs={6} aria-label={t('shipment')}>
																<OrderDetailStepper
																	stepOrdered={t('step_ordered')}
																	stepInProcess={t('step_in_process')}
																	stepShipped={t('step_shipped')}
																	stepDelivered={t('step_delivered')}
																	stepInvoiced={t('step_invoiced')}
																	dateOrdered={shipmentDates.dateOrdered}
																	dateInProcess={shipmentDates.dateInProcess}
																	dateShipped={shipmentDates.dateShipped}
																	dateDelivered={shipmentDates.dateDelivered}
																	dateInvoiced={shipmentDates.dateInvoiced}
																	reachedOrdered={
																		statusHistoryReached.reachedOrdered
																	}
																	reachedInProcess={
																		statusHistoryReached.reachedInProcess
																	}
																	reachedShipped={
																		statusHistoryReached.reachedShipped
																	}
																	reachedDelivered={
																		statusHistoryReached.reachedDelivered
																	}
																	reachedInvoiced={
																		statusHistoryReached.reachedInvoiced
																	}
																	activeStep={statusHistoryActiveStep}
																	shippingCancelledStatus={
																		shippingStatus === ORDER_STATUS.CANCELLED
																	}
																	error={
																		alertCancelStatus ===
																			ORDER_STATUS.CANCELLED ||
																		alertCancelStatus === ORDER_STATUS.ALERT
																	}
																	alert={
																		alertCancelStatus === ORDER_STATUS.ALERT
																	}
																	cancelled={
																		alertCancelStatus === ORDER_STATUS.CANCELLED
																	}
																/>
															</Grid>
														</Grid>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
									<ShippingDetailsSection
										detail={detail}
										orderNumber={order.order_Number}
										plantCode={order.plant_Code}
									/>
								</React.Fragment>
							);
						})}

					{showStatusHistory &&
						(() => {
							const statusHistoryDates = {
								dateOrdered: getOrderStatusDate(ORDER_STATUS.ORDERED),
								dateInProcess: getOrderStatusDate(ORDER_STATUS.IN_PROGRESS),
								dateShipped: '',
								dateDelivered: '',
								dateInvoiced: '',
							};
							const statusHistoryReached = getStatusHistoryReached();
							const statusHistoryActiveStep = getStatusHistoryActiveStep();

							return (
								<Grid
									item
									xs={12}
									style={{
										marginTop: theme.spacing(-4),
									}}
									aria-label={t('shipment')}
								>
									<Grid
										container
										style={{
											padding: ` 0 ${theme.spacing(4)}`,
										}}
										aria-label={t('shipment')}
									>
										<Grid item xs={12} aria-label={t('shipment')}>
											<Grid container>
												<Grid item xs={12} aria-label={t('shipment')}>
													<Grid
														container
														direction="row"
														justifyContent="left"
														alignItems="flex-start"
														aria-label={t('shipment')}
													>
														<Grid item xs={6} aria-label={t('shipment')}>
															<Grid
																container
																sx={{ my: 2 }}
																variant="heading8"
																style={{
																	marginTop: `${theme.spacing(6.5)}`,
																	marginBottom: `${theme.spacing(2.75)}`,
																}}
															>
																<Grid item aria-label={t('shipment')}>
																	<Typography
																		role="paragraph"
																		aria-label={t('shipment')}
																		style={{ fontWeight: '500' }}
																	>
																		{t('shipment')}
																	</Typography>
																</Grid>
															</Grid>
														</Grid>
													</Grid>
												</Grid>
												<Grid
													item
													xs={12}
													sx={{
														overflowX: 'auto',
														width: '100%',
														maxWidth: {
															xs: '100%',
															sm: '100%',
															md: `calc(100vw - 25.75rem)`,
														},
														marginTop: theme.spacing(-1.15),
													}}
													aria-label={t('order_details')}
												>
													<Grid
														container
														direction="row"
														justifyContent="left"
														alignItems="flex-start"
														aria-label={t('order_details')}
													>
														<Grid item xs={6} aria-label={t('order_details')}>
															<OrderDetailStepper
																stepOrdered={t('step_ordered')}
																stepInProcess={t('step_in_process')}
																stepShipped={t('step_shipped')}
																stepDelivered={t('step_delivered')}
																stepInvoiced={t('step_invoiced')}
																dateOrdered={statusHistoryDates.dateOrdered}
																dateInProcess={statusHistoryDates.dateInProcess}
																dateShipped={statusHistoryDates.dateShipped}
																dateDelivered={statusHistoryDates.dateDelivered}
																dateInvoiced={statusHistoryDates.dateInvoiced}
																reachedOrdered={
																	statusHistoryReached.reachedOrdered
																}
																reachedInProcess={
																	statusHistoryReached.reachedInProcess
																}
																reachedShipped={
																	statusHistoryReached.reachedShipped
																}
																reachedDelivered={
																	statusHistoryReached.reachedDelivered
																}
																reachedInvoiced={
																	statusHistoryReached.reachedInvoiced
																}
																brazilStatus={order.brazilStatus}
																error={
																	alertCancelStatus ===
																		ORDER_STATUS.CANCELLED ||
																	alertCancelStatus === ORDER_STATUS.ALERT
																}
																alert={alertCancelStatus === ORDER_STATUS.ALERT}
																cancelled={
																	alertCancelStatus === ORDER_STATUS.CANCELLED
																}
																activeStep={statusHistoryActiveStep}
															/>
														</Grid>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							);
						})()}

					<Grid item xs={12}>
						<Grid
							container
							style={{
								marginTop: showStatusHistory ? theme.spacing(-0.3) : '',
								padding: `${theme.spacing(4)} ${theme.spacing(
									4
								)} ${theme.spacing(0.75)} ${theme.spacing(4)}`,
							}}
							aria-label={t('order_details')}
						>
							<Grid
								item
								xs={12}
								aria-label={t('order_details')}
								style={{
									marginTop: theme.spacing(1),
								}}
							>
								<FileText
									style={{
										fontSize: theme.palette.font.size6,
										fontWeight: theme.palette.font.weightBold,
										marginBottom: '-.27rem',
										marginRight: '.35rem',
									}}
									aria-label={t('order_details')}
									role="generic"
									aria-hidden="true"
								/>
								<Typography
									variant="heading9"
									aria-label={t('order_details')}
									role="paragraph"
								>
									{t('order_details')}
								</Typography>
								<Divider
									sx={{ my: 1 }}
									aria-label={t('order_details')}
									tabIndex={-1}
									role="presentation"
								/>
							</Grid>
						</Grid>
					</Grid>

					<Grid item xs={12}>
						<Grid
							container
							style={{
								padding: `0 ${theme.spacing(4)}`,
							}}
							aria-label={t('order_details')}
						>
							<Grid
								item
								md={10}
								style={{
									padding: `0 ${theme.spacing(4)}`,
									marginLeft: theme.spacing(-4),
									marginTop: '-.2rem',
								}}
								aria-label={t('order_details')}
							>
								<Grid
									container
									style={{
										padding: `0 `,
									}}
									aria-label={t('order_details')}
								>
									<Grid item lg={6} xs={12} aria-label={t('order_details')}>
										<OrderDetailItemCard
											name={t('bill_to')}
											value={
												<OrderAddressBlock
													name={order.bill_To_Customer_Name}
													streetAddress={order.bill_To_Address}
													city={order.bill_To_City}
													state={order.bill_To_State}
													zipCode={order.bill_To_ZipCode}
												/>
											}
										/>
									</Grid>
									<Grid item lg={6} xs={12} aria-label={t('ship_to')}>
										<OrderDetailItemCard
											name={t('ship_to')}
											value={
												<OrderAddressBlock
													name={order.ship_To_Company_Name}
													streetAddress={order.ship_To_Address}
													city={order.ship_To_City}
													state={order.ship_To_State}
													zipCode={order.ship_To_ZipCode}
												/>
											}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('order_due_date')}
											value={newDate(order.order_Due_Date)}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('ordered_by')}
											value={
												order.order_Customer_Name
													? order.order_Customer_Name
													: '-'
											}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('customer_item_number')}
											value={
												order.customer_Item_Number
													? order.customer_Item_Number
													: '-'
											}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('order_date')}
											value={newDate(order.order_Date)}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('sku_number')}
											value={
												order.design_Number ? (
													<Typography variant="body" color="textPrimary">
														<Link
															component={RouterLink}
															to={`/products/${order.item_Number}`}
														>
															{order.design_Number}
														</Link>
													</Typography>
												) : (
													'-'
												)
											}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('documents')}
											value={
												<Download
													documents={(order.customerDocuments || []).map(
														({ id, type, file_Name: fileName }) => ({
															id,
															type,
															fileName,
														})
													)}
												/>
											}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('description')}
											value={
												order.item_Description ? order.item_Description : '-'
											}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('plant')}
											value={order.plant_Name ? order.plant_Name : '-'}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('quantity_ordered')}
											value={
												typeof order.quantityOrdered === 'number' &&
												order.quantityOrdered !== 0
													? order.quantityOrdered.toString()
													: '-'
											}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('po_line_number')}
											value={order.pO_Line_Number ? order.pO_Line_Number : '-'}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('quantity_produced')}
											value={
												typeof order.quantityProduced === 'number' &&
												order.quantityProduced !== 0
													? order.quantityProduced.toString()
													: '-'
											}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('sales_order_number')}
											value={
												order.sales_Order_Number
													? order.sales_Order_Number
													: '-'
											}
										/>
									</Grid>
									<Grid item lg={6} xs={12}>
										<OrderDetailItemCard
											name={t('total_shipped')}
											value={
												typeof order.quantityShipped === 'number' &&
												order.quantityShipped !== 0
													? order.quantityShipped.toString()
													: '-'
											}
										/>
									</Grid>
									<Grid item lg={6} xs={12}></Grid>

									{!!csr.Email && (
										<Grid item lg={6} xs={12}>
											<OrderDetailContactCard
												fullName={`${csr.FirstName} ${csr.LastName}`}
												firstName={csr.FirstName}
												lastName={csr.LastName}
												title={csr.Title}
												to={csr.Email}
												subject={t('subject', {
													po: order.pO_Number,
													orderNumber: order.order_Number,
												})}
											/>
										</Grid>
									)}
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Container>
			) : (
				<Backdrop
					aria-label={t('no_details_found')}
					tabIndex={-1}
					role="presentation"
					sx={{
						color: 'primary',
						zIndex: theme.zIndex.drawer + 1,
						backgroundColor: 'transparent',
					}}
					open={isError || (!isError && !order && !isLoading)}
				>
					{t('no_details_found')}
				</Backdrop>
			)}
		</>
	);
}
OrderDetails.propTypes = {
	namespace: PropTypes.string.isRequired,
};
export default OrderDetails;
